import type { SettingsState } from '@triedge/staff-settings-domain';

export type RegistrationFeeScope = 'per child' | 'per student' | 'per family' | '';

export type ResolvedRegistrationFee = {
  id: string | null;
  name: string;
  amount: number;
  amountCents: number;
  amountLabel: string;
  scopeLabel: RegistrationFeeScope;
  descriptionLabel: string;
  configured: boolean;
};

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const readText = (value: unknown): string => String(value ?? '').trim();

const formatCurrency = (amountCents: number): string =>
  currency.format(Number.isFinite(amountCents) ? amountCents : 0);

const resolveScopeLabel = (name: string): RegistrationFeeScope => {
  const normalized = name.toLowerCase();
  if (normalized.includes('per family')) return 'per family';
  if (normalized.includes('per student')) return 'per student';
  if (normalized.includes('per child')) return 'per child';
  return '';
};

export const resolveRegistrationFee = (
  settingsState: SettingsState,
): ResolvedRegistrationFee => {
  const matchingFee = settingsState.fees.find((fee) => fee.id === 'fee-registration')
    ?? settingsState.fees.find((fee) => {
      const normalizedName = readText(fee.name).toLowerCase();
      return normalizedName.includes('registration fee');
    });

  if (!matchingFee) {
    return {
      id: null,
      name: 'Registration Fee',
      amount: 0,
      amountCents: 0,
      amountLabel: formatCurrency(0),
      scopeLabel: 'per child',
      descriptionLabel: 'the configured registration fee',
      configured: false,
    };
  }

  const amount = Math.max(0, Math.round(Number(matchingFee.amount) || 0));
  const amountCents = amount * 100;
  const scopeLabel = resolveScopeLabel(matchingFee.name) || 'per child';
  const amountLabel = formatCurrency(amount);
  const configured = amount > 0;

  return {
    id: readText(matchingFee.id) || null,
    name: readText(matchingFee.name) || 'Registration Fee',
    amount,
    amountCents,
    amountLabel,
    scopeLabel,
    descriptionLabel: configured
      ? `${amountLabel} ${scopeLabel}`.trim()
      : 'the configured registration fee',
    configured,
  };
};
